package br.com.vvrefeicoes.portal.feature.operacional

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import br.com.vvrefeicoes.portal.core.ui.theme.AppPalette
import br.com.vvrefeicoes.portal.core.ui.theme.AppShapes
import br.com.vvrefeicoes.portal.core.ui.theme.AppSpacing
import br.com.vvrefeicoes.portal.core.util.MoneyFormatter
import br.com.vvrefeicoes.portal.core.util.MonthCalendar
import br.com.vvrefeicoes.portal.domain.model.EntityFinancials
import br.com.vvrefeicoes.portal.domain.model.MonthlyFinancials
import com.composables.icons.lucide.Banknote
import com.composables.icons.lucide.Lucide
import com.composables.icons.lucide.TrendingDown
import com.composables.icons.lucide.TrendingUp
import io.github.koalaplot.core.line.LinePlot
import io.github.koalaplot.core.style.LineStyle
import io.github.koalaplot.core.xygraph.CategoryAxisModel
import io.github.koalaplot.core.xygraph.Point
import io.github.koalaplot.core.xygraph.XYGraph
import io.github.koalaplot.core.xygraph.rememberAxisContent
import org.jetbrains.compose.resources.stringResource
import portalvv.shared.generated.resources.Res
import portalvv.shared.generated.resources.operational_consolidated_profit
import portalvv.shared.generated.resources.operational_managers_col_expense
import portalvv.shared.generated.resources.operational_managers_col_result
import portalvv.shared.generated.resources.operational_managers_col_revenue
import portalvv.shared.generated.resources.operational_managers_totals_caption
import kotlin.math.abs
import kotlin.math.round

private val CARD_CHART_HEIGHT = 220.dp

@Composable
fun EntityFinancialsGrid(
    items: List<EntityFinancials>,
    modifier: Modifier = Modifier,
) {
    Column(
        modifier = modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(AppSpacing.space16),
    ) {
        items.forEach { item ->
            EntityFinancialsCard(item = item, modifier = Modifier.fillMaxWidth())
        }
    }
}

@Composable
private fun EntityFinancialsCard(
    item: EntityFinancials,
    modifier: Modifier = Modifier,
) {
    val resultPercent = if (item.totalRevenueCents == 0L) 0.0 else item.totalResultCents.toDouble() / item.totalRevenueCents * 100
    Surface(
        modifier = modifier,
        shape = AppShapes.large,
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
    ) {
        Column(
            modifier = Modifier.fillMaxWidth().padding(AppSpacing.space16),
            verticalArrangement = Arrangement.spacedBy(AppSpacing.space12),
        ) {
            Text(
                text = item.name,
                style = MaterialTheme.typography.titleMedium,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
            )
            Text(
                text = stringResource(Res.string.operational_managers_totals_caption),
                style = MaterialTheme.typography.labelSmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
            FinancialsRow(
                icon = Lucide.Banknote,
                iconColor = AppPalette.Success500,
                iconContainerColor = AppPalette.Success100,
                label = stringResource(Res.string.operational_managers_col_revenue),
                value = money(item.totalRevenueCents),
            )
            FinancialsRow(
                icon = Lucide.TrendingDown,
                iconColor = AppPalette.Error500,
                iconContainerColor = AppPalette.Error100,
                label = stringResource(Res.string.operational_managers_col_expense),
                value = money(item.totalExpenseCents),
            )
            FinancialsRow(
                icon = Lucide.TrendingUp,
                iconColor = resultColor(item.totalResultCents),
                iconContainerColor = if (item.totalResultCents < 0) AppPalette.Error100 else AppPalette.Success100,
                label = stringResource(Res.string.operational_managers_col_result),
                value = "${money(item.totalResultCents)} (${percentText2(resultPercent)})",
                valueColor = resultColor(item.totalResultCents),
            )
            SeriesLegend(
                entries = listOf(LegendEntry(AppPalette.Primary500, stringResource(Res.string.operational_consolidated_profit))),
            )
            EntityProfitChart(series = item.monthly, modifier = Modifier.fillMaxWidth().height(CARD_CHART_HEIGHT))
        }
    }
}

@Composable
private fun FinancialsRow(
    icon: ImageVector,
    iconColor: Color,
    iconContainerColor: Color,
    label: String,
    value: String,
    valueColor: Color = MaterialTheme.colorScheme.onSurface,
) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8),
    ) {
        Surface(
            shape = AppShapes.small,
            color = iconContainerColor,
            modifier = Modifier.size(32.dp),
        ) {
            Box(contentAlignment = Alignment.Center) {
                Icon(
                    imageVector = icon,
                    contentDescription = null,
                    tint = iconColor,
                    modifier = Modifier.size(16.dp),
                )
            }
        }
        Text(
            text = label,
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
            maxLines = 1,
            overflow = TextOverflow.Ellipsis,
            modifier = Modifier.weight(1f),
        )
        Text(
            text = value,
            style = MaterialTheme.typography.bodyMedium,
            fontWeight = FontWeight.SemiBold,
            color = valueColor,
            maxLines = 1,
        )
    }
}

@Composable
private fun EntityProfitChart(
    series: List<MonthlyFinancials>,
    modifier: Modifier = Modifier,
) {
    if (series.isEmpty()) {
        Text(
            text = "—",
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
        return
    }
    val labels = series.map { MonthCalendar.shortLabel(it.period) }
    val values = series.map { it.profitPercent.toFloat() }
    val points = series.mapIndexed { index, item -> Point(labels[index], item.profitPercent.toFloat()) }
    val maxValue = values.maxOrNull() ?: 0f
    val minValue = values.minOrNull() ?: 0f
    val span = (maxValue - minValue).let { if (it > 0f) it else 1f }
    val pad = span * 0.5f
    // Folga no topo para o rótulo acima do ponto não ser cortado (mesmo padrão da Visão Geral).
    val yAxis =
        if (minValue < 0f) {
            rememberSignedValueAxis(minValue - pad, maxValue + pad)
        } else {
            rememberValueAxis(maxValue.coerceAtLeast(1f), divisions = 4, headroom = 1.5f)
        }
    XYGraph(
        gridStyle = br.com.vvrefeicoes.portal.core.ui.chart.NoGridStyle,
        xAxisModel = CategoryAxisModel(labels),
        yAxisModel = yAxis,
        xAxisContent = rememberAxisContent(labels = { value -> Text(value, style = MaterialTheme.typography.labelSmall) }),
        yAxisContent = rememberAxisContent(labels = { value -> Text(percentLabel(value.toDouble()), style = MaterialTheme.typography.labelSmall) }),
        modifier = modifier,
    ) {
        LinePlot(
            data = points,
            lineStyle = LineStyle(brush = SolidColor(AppPalette.Primary500), strokeWidth = 2.dp),
            symbol = { point ->
                HoverTooltip(text = percentLabel(point.y.toDouble())) {
                    LinePointLabel(percentLabel(point.y.toDouble()), AppPalette.Primary500)
                }
            },
        )
    }
}

private fun resultColor(cents: Long): Color = if (cents < 0) AppPalette.Error500 else AppPalette.Success500

private fun money(cents: Long): String = "R$ ${MoneyFormatter.formatCents(cents)}"

private fun percentText2(value: Double): String {
    val scaled = round(value * 100).toLong()
    val sign = if (scaled < 0) "-" else ""
    val magnitude = abs(scaled)
    return "$sign${magnitude / 100},${(magnitude % 100).toString().padStart(2, '0')}%"
}

private fun percentLabel(value: Double): String {
    val scaled = round(value * 10).toLong()
    return "${scaled / 10},${abs(scaled % 10)}%"
}
